import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { LandingLayout } from '@/components/layout/LandingLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Landing
const LandingPage = lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })));

// Auth pages
const SignInPage = lazy(() => import('@/pages/auth/SignInPage').then(m => ({ default: m.SignInPage })));
const WaitlistPage = lazy(() => import('@/pages/auth/WaitlistPage').then(m => ({ default: m.WaitlistPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));

// ─── Platform Super Admin pages ───────────────────────────────────────────
// IMPORTANT: super_admin governs the platform; it never sees tenant-internal
// data (identities, AI agents, audit log entries from inside tenants). The
// /admin/* routes deal in platform aggregates only.
const PlatformDashboard = lazy(() => import('@/pages/superadmin/PlatformDashboard').then(m => ({ default: m.PlatformDashboard })));
const TenantsListPage = lazy(() => import('@/pages/superadmin/TenantsListPage').then(m => ({ default: m.TenantsListPage })));
const TenantDetailPage = lazy(() => import('@/pages/superadmin/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })));
const TenantCreatePage = lazy(() => import('@/pages/superadmin/TenantCreatePage').then(m => ({ default: m.TenantCreatePage })));
const PlatformBillingPage = lazy(() => import('@/pages/superadmin/PlatformBillingPage').then(m => ({ default: m.PlatformBillingPage })));
const PlatformAuditPage = lazy(() => import('@/pages/superadmin/PlatformAuditPage').then(m => ({ default: m.PlatformAuditPage })));
const PlatformInsightsPage = lazy(() => import('@/pages/superadmin/PlatformInsightsPage').then(m => ({ default: m.PlatformInsightsPage })));
const PlatformSettingsPage = lazy(() => import('@/pages/superadmin/PlatformSettingsPage').then(m => ({ default: m.PlatformSettingsPage })));
const PlatformUsersPage = lazy(() => import('@/pages/superadmin/PlatformUsersPage').then(m => ({ default: m.PlatformUsersPage })));
const PlatformRolesPage = lazy(() => import('@/pages/superadmin/PlatformRolesPage').then(m => ({ default: m.PlatformRolesPage })));
const ConnectorsPage = lazy(() => import('@/pages/superadmin/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })));
const TenantAdminsPage = lazy(() => import('@/pages/superadmin/TenantAdminsPage').then(m => ({ default: m.TenantAdminsPage })));
const PitchDeckPage = lazy(() => import('@/pages/superadmin/PitchDeckPage').then(m => ({ default: m.PitchDeckPage })));
const StrategyPage = lazy(() => import('@/pages/superadmin/StrategyPage').then(m => ({ default: m.StrategyPage })));
const AdvisorPage = lazy(() => import('@/pages/superadmin/AdvisorPage').then(m => ({ default: m.AdvisorPage })));
const FounderHomePage = lazy(() => import('@/pages/superadmin/FounderHomePage').then(m => ({ default: m.FounderHomePage })));
const FounderCollabPage = lazy(() => import('@/pages/superadmin/FounderCollabPage').then(m => ({ default: m.FounderCollabPage })));
const FounderTrackerPage = lazy(() => import('@/pages/superadmin/FounderTrackerPage').then(m => ({ default: m.FounderTrackerPage })));
const FounderFinancialModelPage = lazy(() => import('@/pages/superadmin/FounderFinancialModelPage').then(m => ({ default: m.FounderFinancialModelPage })));
const SubscriptionsPage = lazy(() => import('@/pages/superadmin/SubscriptionsPage').then(m => ({ default: m.SubscriptionsPage })));
const WaitlistAdminPage = lazy(() => import('@/pages/superadmin/WaitlistAdminPage').then(m => ({ default: m.WaitlistAdminPage })));
const SupportAccessPage = lazy(() => import('@/pages/support-access/SupportAccessPage').then(m => ({ default: m.SupportAccessPage })));

// ─── Tenant (Org) pages ────────────────────────────────────────────────────
// Scoped to a single tenant — the caller's org_id from JWT claims is the
// hard boundary on every backend read.
const OrgDashboard = lazy(() => import('@/pages/organization/OrgDashboard').then(m => ({ default: m.OrgDashboard })));
const HumanIdentitiesPage = lazy(() => import('@/pages/organization/HumanIdentitiesPage').then(m => ({ default: m.HumanIdentitiesPage })));
const ServiceAccountsPage = lazy(() => import('@/pages/organization/ServiceAccountsPage').then(m => ({ default: m.ServiceAccountsPage })));
const AIAgentsPage = lazy(() => import('@/pages/organization/AIAgentsPage').then(m => ({ default: m.AIAgentsPage })));
const AIAgentsGraphPage = lazy(() => import('@/pages/organization/AIAgentsGraphPage').then(m => ({ default: m.AIAgentsGraphPage })));
const CisoDashboardPage = lazy(() => import('@/pages/organization/CisoDashboardPage').then(m => ({ default: m.CisoDashboardPage })));
const NHIOverviewPage = lazy(() => import('@/pages/organization/NHIOverviewPage').then(m => ({ default: m.NHIOverviewPage })));
const IdentityDetailPage = lazy(() => import('@/pages/organization/IdentityDetailPage').then(m => ({ default: m.IdentityDetailPage })));
const ProvidersPage = lazy(() => import('@/pages/organization/ProvidersPage').then(m => ({ default: m.ProvidersPage })));
const AnalyticsPage = lazy(() => import('@/pages/organization/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ThreatCenterPage = lazy(() => import('@/pages/organization/ThreatCenterPage').then(m => ({ default: m.ThreatCenterPage })));
const ActivityLogPage = lazy(() => import('@/pages/organization/ActivityLogPage').then(m => ({ default: m.ActivityLogPage })));
const AIAnalysisPage = lazy(() => import('@/pages/organization/AIAnalysisPage').then(m => ({ default: m.AIAnalysisPage })));
const TokenObservabilityPage = lazy(() => import('@/pages/organization/TokenObservabilityPage').then(m => ({ default: m.TokenObservabilityPage })));
const OrgSettingsPage = lazy(() => import('@/pages/organization/OrgSettingsPage').then(m => ({ default: m.OrgSettingsPage })));
const OrgMembersPage = lazy(() => import('@/pages/organization/OrgMembersPage').then(m => ({ default: m.OrgMembersPage })));
const OrgRolesPage = lazy(() => import('@/pages/organization/OrgRolesPage').then(m => ({ default: m.OrgRolesPage })));
const AiActDashboardPage = lazy(() => import('@/pages/organization/AiActDashboardPage').then(m => ({ default: m.AiActDashboardPage })));
const AiActSystemCreatePage = lazy(() => import('@/pages/organization/AiActSystemCreatePage').then(m => ({ default: m.AiActSystemCreatePage })));
const AiActSystemDetailPage = lazy(() => import('@/pages/organization/AiActSystemDetailPage').then(m => ({ default: m.AiActSystemDetailPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-dark-950">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing / Public routes */}
        <Route element={<LandingLayout />}>
          <Route path="/" element={<LandingPage />} />
        </Route>

        {/* Auth routes */}
        <Route path="/auth/signin" element={<SignInPage />} />
        <Route path="/auth/waitlist" element={<WaitlistPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

        {/* ─── Platform Admin (super_admin + auditor + support_engineer) */}
        {/* All three see the /admin/* surface. Read-only / mutation rights */}
        {/* are enforced server-side per permission. support_engineer's     */}
        {/* tenant access is gated by an active break-glass grant — see     */}
        {/* docs/backend/03-roles-and-access.md.                            */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'auditor', 'support_engineer']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/dashboard" element={<PlatformDashboard />} />
          <Route path="/admin/tenants" element={<TenantsListPage />} />
          <Route path="/admin/tenants/new" element={<TenantCreatePage />} />
          <Route path="/admin/tenants/:orgId" element={<TenantDetailPage />} />
          <Route path="/admin/tenants/:orgId/admins" element={<TenantAdminsPage />} />
          <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/admin/waitlist" element={<WaitlistAdminPage />} />
          <Route path="/admin/billing" element={<PlatformBillingPage />} />
          <Route path="/admin/platform-audit" element={<PlatformAuditPage />} />
          <Route path="/admin/platform-users" element={<PlatformUsersPage />} />
          <Route path="/admin/platform-roles" element={<PlatformRolesPage />} />
          <Route path="/admin/connectors" element={<ConnectorsPage />} />
          <Route path="/admin/platform-insights" element={<PlatformInsightsPage />} />
          <Route path="/admin/support-access" element={<SupportAccessPage />} />
          <Route path="/admin/settings" element={<PlatformSettingsPage />} />

          {/* Legacy redirect: /admin/organizations was the old list page; */}
          {/* /admin/tenants is the new aggregate-only home for it. */}
          <Route path="/admin/organizations" element={<Navigate to="/admin/tenants" replace />} />
        </Route>

        {/* ─── Founder surface ───────────────────────────────────────── */}
        {/* Founder Home + Strategy + Pitch Deck + Advisor. Shared by      */}
        {/* super_admin (so they can curate / co-edit) and founder (their  */}
        {/* exclusive surface — they can NOT see the operational block).   */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'founder']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/founder-home"      element={<FounderHomePage />} />
          <Route path="/admin/founder-collab"    element={<FounderCollabPage />} />
          <Route path="/admin/founder-tracker"   element={<FounderTrackerPage />} />
          <Route path="/admin/financial-model"   element={<FounderFinancialModelPage />} />
          <Route path="/admin/pitch-deck"        element={<PitchDeckPage />} />
          <Route path="/admin/strategy"          element={<StrategyPage />} />
          <Route path="/admin/advisor"           element={<AdvisorPage />} />
        </Route>

        {/* ─── Tenant (Org) ──────────────────────────────────────────── */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['org_admin', 'security_analyst', 'viewer']}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/org/dashboard" element={<OrgDashboard />} />
          <Route path="/org/dashboard/ciso" element={<CisoDashboardPage />} />
          <Route path="/org/identities/human" element={<HumanIdentitiesPage />} />
          <Route path="/org/identities/service" element={<ServiceAccountsPage />} />
          <Route path="/org/identities/ai-agents" element={<AIAgentsPage />} />
          <Route path="/org/identities/ai-agents/graph" element={<AIAgentsGraphPage />} />
          <Route path="/org/identities/nhi" element={<NHIOverviewPage />} />
          {/* Shareable per-identity detail page. Reached from the slide-in
              drawer's "Open full page" link, or pasted as a URL in Slack/Jira. */}
          <Route path="/org/identities/:identityId" element={<IdentityDetailPage />} />
          <Route path="/org/providers" element={<ProvidersPage />} />
          <Route path="/org/analytics" element={<AnalyticsPage />} />
          <Route path="/org/ai-analysis" element={<AIAnalysisPage />} />
          <Route path="/org/token-observability" element={<TokenObservabilityPage />} />
          <Route path="/org/threats" element={<ThreatCenterPage />} />
          <Route path="/org/activity" element={<ActivityLogPage />} />
          <Route path="/org/settings/support-access" element={<SupportAccessPage />} />
          <Route path="/org/settings/members" element={<OrgMembersPage />} />
          <Route path="/org/settings/roles" element={<OrgRolesPage />} />
          <Route path="/org/settings" element={<OrgSettingsPage />} />
          <Route path="/org/ai-act" element={<AiActDashboardPage />} />
          <Route path="/org/ai-act/new" element={<AiActSystemCreatePage />} />
          <Route path="/org/ai-act/:aiSystemId" element={<AiActSystemDetailPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
